import { findFencedBlock } from './factory-questions';

/**
 * Sub-tasks the AI builder proposes for its task (asked to split the work, or when it sees the task is
 * really several). Its final report ends with one fenced block,
 *
 * ```factory-subtasks
 * { "subtasks": [{ "title": "Asana adapter", "detail": "…" }] }
 * ```
 *
 * The task shows them above its sub-tasks, all ticked; a person unticks any they don't want and adds
 * the rest in one go, as sub-tasks (T-42) of the task. The factory records what was added as one chat
 * message (formatSubtaskDecisions), which the card reads back (parseSubtaskDecisions).
 */

export const FACTORY_SUBTASKS_LANG = 'factory-subtasks';

/** Most sub-tasks in one block, and characters per piece of text. */
export const FACTORY_SUBTASKS_MAX = 20;
const TITLE_MAX = 200;
const DETAIL_MAX = 4000;

export interface FactorySubtask {
  title: string;
  /** What it covers: the new sub-task's description. */
  detail?: string;
}

export interface FactorySubtasks {
  subtasks: FactorySubtask[];
}

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. A bare list (of objects or titles) is accepted too. */
export function parseFactorySubtasksJson(raw: string): FactorySubtasks | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const obj = (Array.isArray(data) ? { subtasks: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.subtasks)) return null;
  const subtasks: FactorySubtask[] = [];
  for (const item of obj.subtasks.slice(0, FACTORY_SUBTASKS_MAX)) {
    const s = (typeof item === 'string' ? { title: item } : (item ?? {})) as Record<string, unknown>;
    // Titles are one line: they're the key the decisions message shows.
    const title = text(s.title, TITLE_MAX).replace(/\s*\n\s*/g, ' ');
    if (!title) continue;
    const detail = text(s.detail ?? s.description, DETAIL_MAX);
    subtasks.push({ title, ...(detail ? { detail } : {}) });
  }
  return subtasks.length ? { subtasks } : null;
}

/** The first factory-subtasks block in a message, with where it sits; null when there's none (or it's broken). */
export function findFactorySubtasks(message: string): { subtasks: FactorySubtasks; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_SUBTASKS_LANG);
  const subtasks = block ? parseFactorySubtasksJson(block.body) : null;
  if (!block || !subtasks) return null;
  return { subtasks, start: block.start, end: block.end };
}

/** What happened to one proposed sub-task: added (with its ref, SF-T-60) or left out. `number` is its place in the block (1-based). */
export interface SubtaskDecision {
  number: number;
  title: string;
  /** The sub-task made; null when it was left out. */
  ref: string | null;
}

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();
const cleanTitle = (s: string) => oneLine(s).replace(/\*\*/g, '');

/** The decisions message's first line: which run's proposal it is (null: the newest before it). */
export function subtaskDecisionsHeader(runNumber: number | null): string {
  return runNumber === null ? '**Proposed sub-tasks**' : `**Proposed sub-tasks from R-${runNumber}**`;
}

/** The decisions as the chat message posted for the person who added them: `1. **Title** → Added: SF-T-60`, or `→ Left out`. */
export function formatSubtaskDecisions(runNumber: number | null, decisions: SubtaskDecision[]): string {
  const lines = [subtaskDecisionsHeader(runNumber), ''];
  for (const d of decisions) lines.push(`${d.number}. **${cleanTitle(d.title)}** → ${d.ref ? `Added: ${d.ref}` : 'Left out'}`);
  return lines.join('\n');
}

const HEADER_RE = /^\*\*Proposed sub-tasks(?: from R-(\d+))?\*\*$/;
const LINE_RE = /^(\d+)\.\s+\*\*(.*?)\*\*\s+→\s+(?:Added:\s*((?:[A-Z]{2}-)?T-\d+)|Left out)$/;

/** Reads back a message formatSubtaskDecisions wrote; null when the message isn't one. */
export function parseSubtaskDecisions(message: string): { runNumber: number | null; decisions: SubtaskDecision[] } | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  const header = HEADER_RE.exec(lines[0]?.trim() ?? '');
  if (!header) return null;
  const decisions: SubtaskDecision[] = [];
  for (const line of lines.slice(1)) {
    const m = LINE_RE.exec(line.trim());
    if (m) decisions.push({ number: Number(m[1]), title: m[2], ref: m[3] ?? null });
  }
  return decisions.length ? { runNumber: header[1] ? Number(header[1]) : null, decisions } : null;
}

/** One run's proposed sub-tasks on a task, and what was added (null until someone decides: every one is decided at once). */
export interface SubtaskProposalOf<M> {
  /** The factory's message with the factory-subtasks block. */
  message: M;
  run: number | null;
  subtasks: FactorySubtasks;
  /** By number (1-based). */
  decided: Map<number, SubtaskDecision> | null;
}

const PROPOSAL_RUN_RE = /^Factory · R-(\d+)$/;

/**
 * A task's sub-task proposals, oldest first, from its messages in order (oldest first): the factory's
 * (`label` set, "Factory · R-12") with a factory-subtasks block, and people's decisions messages
 * (`label` null, formatSubtaskDecisions). A decisions message belongs to the proposal of the run it
 * names; one naming no run, to the newest before it. The task page and the Inbox (SF-T-178) read them so.
 */
export function subtaskProposalsOf<M extends { body: string | null; label: string | null }>(messages: M[]): SubtaskProposalOf<M>[] {
  const proposals: SubtaskProposalOf<M>[] = [];
  for (const message of messages) {
    if (!message.body) continue;
    if (message.label) {
      const found = findFactorySubtasks(message.body);
      const run = PROPOSAL_RUN_RE.exec(message.label)?.[1];
      if (found) proposals.push({ message, run: run ? Number(run) : null, subtasks: found.subtasks, decided: null });
      continue;
    }
    const parsed = parseSubtaskDecisions(message.body);
    if (!parsed) continue;
    const proposal = parsed.runNumber === null ? proposals[proposals.length - 1] : [...proposals].reverse().find((p) => p.run === parsed.runNumber);
    if (proposal && !proposal.decided) proposal.decided = new Map(parsed.decisions.map((d) => [d.number, d]));
  }
  return proposals;
}
